import { defineComponent as _defineComponent } from 'vue'
import {
  Company,
  CreateEmployeeMessageDataChannelEnum,
  Employee as TEmployee,
  EmployeeMessageStats,
  EmployeeStatusEnum,
  User as TUser,
} from '@/../api/v1';
import { employeeMessageApi } from '@/api';
import Button from '@/components/buttons/Button.vue';
import ActionDialog from '@/components/dialog/ActionDialog.vue';
import Dialog from '@/components/dialog/Dialog.vue';
import UnsavedChangesDialog from '@/components/dialog/UnsavedChangesDialog.vue';
import EmployeeAvatar from '@/components/employee/EmployeeAvatar.vue';
import InputGroup from '@/components/form/InputGroup.vue';
import ToolTipWrapper from '@/components/info/ToolTipWrapper.vue';
import AutoComplete from '@/components/inputs/AutoComplete.vue';
import Checkbox from '@/components/inputs/Checkbox.vue';
import TextArea from '@/components/inputs/TextArea.vue';
import TextInput from '@/components/inputs/TextInput.vue';
import Dot from '@/components/interface/Dot.vue';
import LoadingDot from '@/components/loaders/LoadingDot.vue';
import LoadingOverlay from '@/components/loaders/LoadingOverlay.vue';
import i18 from '@/i18n';
import { useOpen } from '@/lib/composition/dialog';
import { withLoading } from '@/lib/composition/loading';
import {
  employeeActive,
  employeePending,
  fullName,
} from '@/lib/employee/employeeFunctions';
import { AvatarSizes } from '@/lib/enum/AvatarSizes';
import { Icon, IconType } from '@/lib/enum/Icon';
import { Entity } from '@/lib/realtime/RealtimeEntity';
import { realtimeQuery } from '@/lib/realtime/weak/realtimeFunctions';
import { showSuccess } from '@/plugins/ToastNotifications';
import {
  email,
  getErrorMessages,
  maxLength,
  required,
} from '@/plugins/vuelidate';
import store from '@/store';
import { getEntity } from '@/util/entityFunctions';
import { viewport } from '@/util/windowFunctions';
import DialogHeader from '@/views/settings/modals/DialogHeader.vue';
import useVuelidate from '@vuelidate/core';
import { computed, onMounted, reactive, ref, watchEffect } from 'vue';

enum ToEntityEnum {
  Employee = 'employee',
  Group = 'group',
  Location = 'location',
}
type ToEntity = typeof ToEntityEnum[keyof typeof ToEntityEnum];
type EntityItem = {
  id: number;
  name: string;
  entity: ToEntity;
  icon: IconType | null;
};
type SendToItem = EntityItem | { header: string } | { divider: true };


export default /*#__PURE__*/_defineComponent({
  __name: 'SendMessageDialog',
  props: {
    value: { type: Boolean, default: false },
    employeeIds: { default: () => [] }
  },
  emits: ["input", "close"],
  setup(__props: any, { emit }: { emit: ({
  (event: 'input', value: boolean): void;
  (event: 'close'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { value: boolean, employeeIds: number[] }

const isEntityItem = (item: SendToItem): item is EntityItem => item?.id != null;

const everyoneItem: EntityItem = {
  id: 0,
  name: String(i18.t('label.everyone')),
  entity: ToEntityEnum.Employee,
  icon: null,
};

const getItemIds = (
  to: SendToItem[],
  entity: ToEntity | null = null,
): number[] =>
  to
    .filter(
      (i: SendToItem) =>
        isEntityItem(i) && (!entity || (i.entity && i.entity === entity)),
    )
    .map((i: EntityItem) => i.id);

const stripHtmlElements = (input: string): string =>
  input.trim().replace(/(<([^>]+)>)/gi, '');





const open = useOpen(() => props.value, emit);

const loggedInEmployee = computed<TEmployee>(
  () => store.getters.loggedInEmployee,
);
const authUser = computed<TUser>(() => store.state.authenticatedUser);
const currentCompany = computed<Company>(() => store.getters.currentCompany);

const employees = realtimeQuery(Entity.Employee)
  .whereIn('status', [EmployeeStatusEnum.Active, EmployeeStatusEnum.Pending])
  .whereNotIn('id', [store.getters.loggedInEmployee.id])
  .fetch();
const groups = realtimeQuery(Entity.EmployeeGroup).fetch();
const locations = realtimeQuery(Entity.Location).whereActive().fetch();

const messageStats = reactive<{
  isLoading: boolean;
  data: EmployeeMessageStats | null;
}>({
  isLoading: true,
  data: null,
});

onMounted(async () => {
  await employeeMessageApi
    .showEmployeeMessageStats()
    .then(({ data }) => {
      messageStats.data = data;
    })
    .finally(() => {
      messageStats.isLoading = false;
    });
});

const initialising = computed(
  () =>
    employees.isLoading ||
    locations.isLoading ||
    groups.isLoading ||
    messageStats.isLoading,
);

const sendToList = computed<SendToItem[]>(() => {
  return [
    {
      header: currentCompany.value.name.toUpperCase(),
    },
    everyoneItem,
    {
      divider: true,
    },
    {
      header: i18.tc('entities.location', 1).toUpperCase(),
    },
    ...locations.data.map<SendToItem>((l) => ({
      id: l.id,
      name: l.name,
      entity: ToEntityEnum.Location,
      icon: Icon.LocationDot,
    })),
    {
      divider: true,
    },
    {
      header: i18.tc('label.group', 1).toUpperCase(),
    },
    ...groups.data.map<SendToItem>((g) => ({
      id: g.id,
      name: g.name,
      entity: ToEntityEnum.Group,
      icon: Icon.Users,
    })),
    {
      divider: true,
    },
    {
      header: i18.tc('entities.employee', 1).toUpperCase(),
    },
    ...employees.data.filter(employeeActive).map<SendToItem>((e) => ({
      id: e.id,
      name: fullName(e),
      entity: ToEntityEnum.Employee,
      icon: Icon.User,
    })),
  ];
});

const toQuery = ref('');

const showSaveChangesDialog = ref(false);

const showMessageLimitDialog = ref(false);
watchEffect(() => {
  showMessageLimitDialog.value = messageStats.data?.sendLimitReached || false;
});

const formValues = reactive({
  to: [everyoneItem],
  subject: '',
  body: '',
  mailReplyAddress: '',
});
watchEffect(() => {
  if (props.employeeIds.length) {
    formValues.to = sendToList.value.filter(
      (i): i is EntityItem =>
        isEntityItem(i) &&
        i.entity === ToEntityEnum.Employee &&
        props.employeeIds.includes(i.id),
    );
  }
  formValues.mailReplyAddress =
    loggedInEmployee.value.contactEmail || authUser.value.email;
});
const validations = {
  to: { required },
  subject: { required, maxLength: maxLength(255) },
  body: { required },
  mailReplyAddress: { required, email },
};
const form = useVuelidate(validations, formValues, { $autoDirty: true });

const tryClose = () => {
  if (form.value.$anyDirty) {
    showSaveChangesDialog.value = true;
  } else {
    open.value = false;
  }
};

const selectedEmployeeIds = computed(() => {
  const ids = getItemIds(formValues.to, ToEntityEnum.Employee);
  return ids.includes(0) ? employees.data.map((e) => e.id) : ids;
});
const selectedLocationIds = computed(() =>
  getItemIds(formValues.to, ToEntityEnum.Location),
);
const selectedGroupIds = computed(() =>
  getItemIds(formValues.to, ToEntityEnum.Group),
);
const recipients = computed(() =>
  employees.data.filter(
    (e) =>
      selectedEmployeeIds.value.includes(e.id) ||
      selectedLocationIds.value.some((l) => e.locationIds.includes(l)) ||
      selectedGroupIds.value.includes(e.employeeGroupId),
  ),
);
const activeRecipients = computed(() =>
  recipients.value.filter(employeeActive),
);
const pendingRecipients = computed(() =>
  recipients.value.filter(employeePending),
);

const onToUpdate = (v: SendToItem[]) => {
  const itemIds = getItemIds(v);
  // If there's no selected items and the form doesn't include everyone already then set to everyone
  // If everyone is cleared, the input becomes empty
  if (
    (!itemIds.length && !formValues.to.some((i) => i.id === everyoneItem.id)) ||
    itemIds[itemIds.length - 1] === everyoneItem.id
  ) {
    form.value.to.$model = [everyoneItem];
    return;
  }
  // If an item is clicked, then remove 'everyone' option
  form.value.to.$model = v.filter<EntityItem>(
    (i: SendToItem): i is EntityItem =>
      isEntityItem(i) && i.id !== everyoneItem.id,
  );
};

const sending = ref(false);
const submit = withLoading(sending, async () => {
  await employeeMessageApi
    .createEmployeeMessage({
      createEmployeeMessageData: {
        channel: CreateEmployeeMessageDataChannelEnum.Mail,
        mailReplyAddress: formValues.mailReplyAddress,
        subject: stripHtmlElements(formValues.subject),
        body: stripHtmlElements(formValues.body),
        employeeIds: activeRecipients.value.map((e) => e.id),
        groupIds: selectedGroupIds.value,
        locationIds: selectedLocationIds.value,
      },
    })
    .then(() => {
      showSuccess(i18.tc('successMessage.messageSent'));
      open.value = false;
    });
});

return { __sfc: true,ToEntityEnum, isEntityItem, everyoneItem, getItemIds, stripHtmlElements, props, emit, open, loggedInEmployee, authUser, currentCompany, employees, groups, locations, messageStats, initialising, sendToList, toQuery, showSaveChangesDialog, showMessageLimitDialog, formValues, validations, form, tryClose, selectedEmployeeIds, selectedLocationIds, selectedGroupIds, recipients, activeRecipients, pendingRecipients, onToUpdate, sending, submit, Button, ActionDialog, Dialog, UnsavedChangesDialog, EmployeeAvatar, InputGroup, ToolTipWrapper, AutoComplete, Checkbox, TextArea, TextInput, Dot, LoadingDot, LoadingOverlay, fullName, AvatarSizes, Icon, getErrorMessages, getEntity, viewport, DialogHeader }
}

})